/**
 * Drawer — bottom-anchored modal overlay (web equivalent of RN
 * `DrawerOverlay` / `ItineraryDrawer` / `SettingsDrawer`). Used for any nav
 * tab content; opened via `useDrawer().openDrawer(id)`. Routes are NOT used.
 */
import React, { useEffect } from 'react';
import './Drawer.css';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Header title row (left). */
  title?: React.ReactNode;
  /** Header right slot (e.g. action buttons). */
  headerRight?: React.ReactNode;
  /** Variant — `bottom-sheet` slides up from bottom; `full` covers full viewport. */
  variant?: 'bottom-sheet' | 'full';
  children: React.ReactNode;
}

const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  headerRight,
  variant = 'bottom-sheet',
  children,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('wg-drawer-locked');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('wg-drawer-locked');
    };
  }, [open, onClose]);

  return (
    <div
      className={`wg-drawer-root${open ? ' is-open' : ''}`}
      aria-hidden={!open}
    >
      <div className="wg-drawer-scrim" onClick={onClose} />
      <div
        className={`wg-drawer wg-drawer--${variant}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="wg-drawer__handle" aria-hidden />
        <header className="wg-drawer__header">
          <div className="wg-drawer__title">{title}</div>
          <div className="wg-drawer__header-right">
            {headerRight}
            <button
              type="button"
              className="wg-drawer__close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </header>
        <div className="wg-drawer__content">{children}</div>
      </div>
    </div>
  );
};

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export default Drawer;
